import React from "react";

import { formatAccountLevelMentionsInText } from "../../Utils/sponsorTipsConstants";

type TipMarkdownContentProps = {
  markdown: string;
  className?: string;
};

/** Lightweight markdown renderer for tip body_markdown (tables + paragraphs). */
const TipMarkdownContent: React.FC<TipMarkdownContentProps> = ({
  markdown,
  className = "",
}) => {
  const blocks = formatAccountLevelMentionsInText(markdown).split(/\n\n+/);

  return (
    <div className={`space-y-4 text-base text-gray-700 ${className}`}>
      {blocks.map((block, idx) => {
        const trimmed = block.trim();
        if (!trimmed) return null;

        if (trimmed.startsWith("|")) {
          const rows = trimmed
            .split("\n")
            .filter(r => r.trim().startsWith("|"));
          if (rows.length < 2) {
            return (
              <p key={idx} className="m-0 whitespace-pre-wrap">
                {trimmed}
              </p>
            );
          }
          const parseRow = (row: string) =>
            row
              .split("|")
              .slice(1, -1)
              .map(c => c.trim());
          const header = parseRow(rows[0]);
          const bodyRows = rows.slice(2).map(parseRow);

          return (
            <div
              key={idx}
              className="overflow-x-auto rounded-lg border border-gray-200"
            >
              <table className="min-w-full text-left text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {header.map((cell, i) => (
                      <th
                        key={i}
                        className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-900"
                      >
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bodyRows.map((row, ri) => (
                    <tr
                      key={ri}
                      className="border-b border-gray-100 last:border-0"
                    >
                      {row.map((cell, ci) => (
                        <td key={ci} className="px-3 py-2 text-gray-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        return (
          <p key={idx} className="m-0 whitespace-pre-wrap leading-relaxed">
            {trimmed}
          </p>
        );
      })}
    </div>
  );
};

export default TipMarkdownContent;
